import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Building2,
  List as ListIcon,
  Plus,
  Search,
  Pencil,
  X,
  RotateCcw,
  Loader2,
  UserCircle2,
  ChevronLeft,
  ChevronRight,
  Save,
  CheckCircle2,
  XCircle,
  Clock,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'react-toastify';
import corporateApi from '../../services/corporateApi';
import ccOnboardingApi, { currentUserCode, currentUserName } from '../../services/ccOnboardingApi';

const INDIAN_STATES = [
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chhattisgarh',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi',
  'Jammu and Kashmir',
  'Ladakh',
  'Lakshadweep',
  'Puducherry',
];

function formatDate(dateString) {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return String(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return String(dateString);
  }
}

export default function CorporateList({
  initialView = 'form',
  initialCorporate = null,
  readOnly = false,
  onExit = null,
  onApprove = null,
  onReject = null,
  approving = false,
  rejecting = false,
} = {}) {
  const [view, setView] = useState(initialView);
  const [formMode, setFormMode] = useState(initialCorporate ? 'edit' : 'create');

  const [corporateList, setCorporateList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [stateList, setStateList] = useState(INDIAN_STATES);

  const [filters, setFilters] = useState({
    code: '',
    name: '',
    email: '',
    mobile: '',
    approvalStatus: '',
    status: '',
  });

  const initialFormData = {
    id: null,
    code: '',
    name: '',
    userCode: '',
    userName: '',
    status: 1,
    isApproved: 0,
    createdBy: '',
    createdDate: '',
    modifiedBy: '',
    modifiedDate: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    stateName: '',
    pincode: '',
    pannumber: '',
    mobile: '',
    email: '',
    bankName: '',
    accountName: '',
    accountNumber: '',
    ifsc: '',
    bankLocation: '',
    sendReportToPatient: 'N',
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    if (initialCorporate) {
      setFormData({
        ...initialFormData,
        ...initialCorporate,
        userCode: initialCorporate.userCode || '',
        userName: initialCorporate.userName || initialCorporate.username || '',
        state: initialCorporate.state || initialCorporate.stateName || '',
        stateName: initialCorporate.stateName || initialCorporate.state || '',
        sendReportToPatient:
          initialCorporate.sendReportToPatient !== undefined && initialCorporate.sendReportToPatient !== null
            ? String(initialCorporate.sendReportToPatient).toUpperCase() === 'Y' ||
              String(initialCorporate.sendReportToPatient).toUpperCase() === 'YES' ||
              initialCorporate.sendReportToPatient === 1 ||
              initialCorporate.sendReportToPatient === '1'
              ? 'Y'
              : 'N'
            : 'N',
      });
      setStatusCheckbox(initialCorporate.status === 0);
      setFormMode('edit');
      setView('form');
    }
  }, [initialCorporate]);
  const [corporateUsers, setCorporateUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [statusCheckbox, setStatusCheckbox] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Click outside listener for filter popup
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterOpen]);

  // Fetch States using Onboarding State API
  const fetchStates = useCallback(async () => {
    try {
      const res = await ccOnboardingApi.getStateList();
      const list = res?.data?.data || res?.data || res || [];
      const raw = Array.isArray(list) ? list : [];
      const names = raw
        .map((s) => (typeof s === 'string' ? s : s.stateName ?? s.description ?? s.name ?? s.label))
        .filter(Boolean);
      if (names.length) {
        setStateList(names);
      }
    } catch {
      setStateList(INDIAN_STATES);
    }
  }, []);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  // Fetch Corporate Users (COORPERATE_MANAGER role)
  const fetchCorporateUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await corporateApi.getCorporateUsers('COORPERATE_MANAGER', 0, 50);
      const list =
        res?.responseData?.content ||
        res?.data?.responseData?.content ||
        res?.content ||
        [];
      if (Array.isArray(list)) {
        setCorporateUsers(list);
      }
    } catch (err) {
      console.error('Failed to load corporate users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchCorporateUsers();
  }, [fetchCorporateUsers]);

  // Ensure selected userCode is always in the dropdown options even if not in page 0 list
  const userOptions = useMemo(() => {
    const options = [...corporateUsers];
    if (formData.userCode && !options.some((u) => u.userCode === formData.userCode)) {
      options.unshift({
        userCode: formData.userCode,
        userName: formData.userName || formData.userCode,
      });
    }
    return options;
  }, [corporateUsers, formData.userCode, formData.userName]);

  const handleUserSelect = (selectedCode) => {
    const selected = userOptions.find((u) => u.userCode === selectedCode);
    setFormData((prev) => ({
      ...prev,
      userCode: selectedCode,
      userName: selected
        ? selected.userName ||
          selected.username ||
          `${selected.firstName || ''} ${selected.lastName || ''}`.trim() ||
          selectedCode
        : '',
    }));
  };

  const loadCorporateList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        page: page,
        size: pageSize,
      };

      if (filters.code.trim()) {
        payload.code = filters.code.trim();
      }
      if (filters.name.trim()) {
        payload.name = filters.name.trim();
      }
      if (filters.email.trim()) {
        payload.email = filters.email.trim();
      }
      if (filters.mobile.trim()) {
        payload.mobile = filters.mobile.trim();
      }
      if (filters.status !== undefined && filters.status !== '') {
        payload.status = filters.status;
      }
      if (filters.approvalStatus !== '') {
        payload.isApproved = filters.approvalStatus;
      }

      const res = await corporateApi.getCorporateList(payload);
      let list = [];
      let total = 0;

      if (Array.isArray(res?.content)) {
        list = res.content;
        total = res.page?.totalElements ?? res.totalElements ?? res.content.length;
      } else if (Array.isArray(res?.data?.content)) {
        list = res.data.content;
        total = res.data.page?.totalElements ?? res.data.totalElements ?? res.data.content.length;
      } else if (Array.isArray(res?.data)) {
        list = res.data;
        total = res.totalElements ?? res.data.length;
      } else if (Array.isArray(res)) {
        list = res;
        total = res.length;
      }

      setCorporateList(list);
      setTotalRecords(total);
    } catch (err) {
      console.error('Error loading corporate list:', err);
      toast.error('Failed to load corporate list');
      setCorporateList([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    if (view === 'list') {
      loadCorporateList();
    }
  }, [view, loadCorporateList]);

  const handleAddNew = () => {
    setFormData({
      ...initialFormData,
      createdBy: currentUserCode() || 'ADMIN',
      createdDate: new Date().toISOString(),
    });
    setStatusCheckbox(false);
    setFormMode('create');
    setView('form');
  };

  const handleViewEdit = (corporate) => {
    setFormData({
      ...corporate,
      userCode: corporate.userCode || '',
      userName: corporate.userName || corporate.username || '',
      state: corporate.state || corporate.stateName || '',
      stateName: corporate.stateName || corporate.state || '',
      sendReportToPatient:
        corporate.sendReportToPatient !== undefined && corporate.sendReportToPatient !== null
          ? String(corporate.sendReportToPatient).toUpperCase() === 'Y' ||
            String(corporate.sendReportToPatient).toUpperCase() === 'YES' ||
            corporate.sendReportToPatient === 1 ||
            corporate.sendReportToPatient === '1'
            ? 'Y'
            : 'N'
          : 'N',
    });
    setStatusCheckbox(corporate.status === 0);
    setFormMode('edit');
    setView('form');
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const name = formData.name ? formData.name.trim() : '';
    const email = formData.email ? formData.email.trim() : '';
    const mobile = formData.mobile ? formData.mobile.trim() : '';
    const pincode = formData.pincode ? String(formData.pincode).trim() : '';
    const pan = formData.pannumber ? formData.pannumber.trim().toUpperCase() : '';
    const bankName = formData.bankName ? formData.bankName.trim() : '';
    const accountName = formData.accountName ? formData.accountName.trim() : '';
    const accountNumber = formData.accountNumber ? formData.accountNumber.trim() : '';
    const ifsc = formData.ifsc ? formData.ifsc.trim().toUpperCase() : '';
    const bankLocation = formData.bankLocation ? formData.bankLocation.trim() : '';

    if (!name) {
      toast.error('Please enter Corporate Name');
      return false;
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return false;
    }

    if (mobile && !/^\d{10}$/.test(mobile)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return false;
    }

    if (pincode && !/^\d{6}$/.test(pincode)) {
      toast.error('Please enter a valid 6-digit pincode');
      return false;
    }

    if (!pan) {
      toast.error('Please enter PAN Number');
      return false;
    }

    if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) {
      toast.error('Please enter a valid 10-character PAN number (e.g. ABCDE1234F)');
      return false;
    }

    if (ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) {
      toast.error('Please enter a valid 11-character IFSC Code (e.g. SBIN0001234)');
      return false;
    }

    return true;
  };

  const handleSaveCorporate = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        name: formData.name.trim(),
        userCode: formData.userCode ? formData.userCode.trim() : '',
        userName: formData.userName ? formData.userName.trim() : '',
        email: formData.email ? formData.email.trim() : '',
        mobile: formData.mobile ? formData.mobile.trim() : '',
        pincode: formData.pincode ? String(formData.pincode).trim() : '',
        pannumber: formData.pannumber ? formData.pannumber.trim().toUpperCase() : '',
        bankName: formData.bankName ? formData.bankName.trim() : '',
        accountName: formData.accountName ? formData.accountName.trim() : '',
        accountNumber: formData.accountNumber ? formData.accountNumber.trim() : '',
        ifsc: formData.ifsc ? formData.ifsc.trim().toUpperCase() : '',
        bankLocation: formData.bankLocation ? formData.bankLocation.trim() : '',
        sendReportToPatient:
          String(formData.sendReportToPatient).toUpperCase() === 'Y' ||
          String(formData.sendReportToPatient).toUpperCase() === 'YES' ||
          formData.sendReportToPatient === 1
            ? 'Y'
            : 'N',
        createdBy: currentUserCode() || 'ADMIN',
        createdDate: new Date().toISOString(),
        status: 1,
        isApproved: 0,
        modifiedBy: '',
        modifiedDate: '',
        state: formData.state || '',
        stateName: formData.stateName || formData.state || '',
      };

      const res = await corporateApi.saveCorporate(payload);
      if (res) {
        toast.success('Corporate created successfully!');
        setFormData(initialFormData);
        setView('list');
      } else {
        toast.error('Failed to create corporate');
      }
    } catch (err) {
      console.error('Error saving corporate:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to save corporate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCorporateStatus = async () => {
    if (!formData.id) {
      toast.error('Invalid corporate data');
      return;
    }

    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const newStatus = statusCheckbox ? 0 : 1;
      const payload = {
        ...formData,
        name: formData.name ? formData.name.trim() : '',
        userCode: formData.userCode ? formData.userCode.trim() : '',
        userName: formData.userName ? formData.userName.trim() : '',
        email: formData.email ? formData.email.trim() : '',
        mobile: formData.mobile ? formData.mobile.trim() : '',
        pincode: formData.pincode ? String(formData.pincode).trim() : '',
        pannumber: formData.pannumber ? formData.pannumber.trim().toUpperCase() : '',
        bankName: formData.bankName ? formData.bankName.trim() : '',
        accountName: formData.accountName ? formData.accountName.trim() : '',
        accountNumber: formData.accountNumber ? formData.accountNumber.trim() : '',
        ifsc: formData.ifsc ? formData.ifsc.trim().toUpperCase() : '',
        bankLocation: formData.bankLocation ? formData.bankLocation.trim() : '',
        sendReportToPatient:
          String(formData.sendReportToPatient).toUpperCase() === 'Y' ||
          String(formData.sendReportToPatient).toUpperCase() === 'YES' ||
          formData.sendReportToPatient === 1
            ? 'Y'
            : 'N',
        status: newStatus,
        modifiedBy: currentUserCode() || 'ADMIN',
        modifiedDate: new Date().toISOString(),
        stateName: formData.stateName || formData.state || '',
      };

      const res = await corporateApi.updateCorporate(formData.id, payload);
      if (res) {
        toast.success(`Corporate ${newStatus === 1 ? 'activated' : 'deactivated'} successfully!`);
        setView('list');
      } else {
        toast.error('Failed to update corporate status');
      }
    } catch (err) {
      console.error('Error updating corporate status:', err);
      toast.error(err.response?.data?.message || err.message || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.code.trim()) count++;
    if (filters.name.trim()) count++;
    if (filters.email.trim()) count++;
    if (filters.mobile.trim()) count++;
    if (filters.approvalStatus !== '') count++;
    if (filters.status !== undefined && filters.status !== '') count++;
    return count;
  }, [filters]);

  const handleResetFilters = () => {
    setFilters({
      code: '',
      name: '',
      email: '',
      mobile: '',
      approvalStatus: '',
      status: '',
    });
    setPage(0);
  };

  const handleSearchSubmit = (e) => {
    if (e) e.preventDefault();
    setPage(0);
    loadCorporateList();
  };

  const totalPages = Math.ceil(totalRecords / pageSize) || 1;

  const formContent = (
    <div className="mx-auto max-w-2xl">
      <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-xs border border-slate-200 ring-1 ring-slate-200/80">
        {(formMode === 'edit' || readOnly) && (
          <div className="mb-6 flex items-center justify-between rounded-xl bg-slate-50 p-3.5 border border-slate-200">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Corporate Status</span>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  formData.status === 1 ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
                }`}
              >
                {formData.status === 1 ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                {formData.status === 1 ? 'Active' : 'Inactive'}
              </span>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  formData.isApproved === 1
                    ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                    : formData.isApproved === 2
                    ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
                    : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                }`}
              >
                <Clock className="h-3.5 w-3.5" />
                {formData.isApproved === 1 ? 'Approved' : formData.isApproved === 2 ? 'Rejected' : 'Pending'}
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-center">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (readOnly) return;
              formMode === 'create' ? handleSaveCorporate() : handleUpdateCorporateStatus();
            }}
            className="w-full max-w-xl space-y-4 text-left"
          >
            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Corporate Code
              </label>
              <input
                type="text"
                readOnly
                disabled
                value={formData.code || ''}
                placeholder="Auto-generated"
                className="w-full rounded-xl border border-slate-300 bg-slate-50 px-3.5 py-2.5 text-xs text-slate-500 outline-none cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Corporate Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                disabled={readOnly}
                readOnly={readOnly}
                maxLength={100}
                value={formData.name || ''}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="e.g. Sunrise Corporate"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                User Name
              </label>
              <select
                value={formData.userCode || ''}
                onChange={(e) => handleUserSelect(e.target.value)}
                disabled={readOnly || loadingUsers}
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs cursor-pointer disabled:bg-slate-100 disabled:cursor-not-allowed"
              >
                <option value="">{loadingUsers ? 'Loading users...' : 'Select User Name'}</option>
                {userOptions.map((u) => {
                  const label =
                    u.userName ||
                    u.username ||
                    `${u.firstName || ''} ${u.lastName || ''}`.trim() ||
                    u.userCode;
                  return (
                    <option key={u.userCode} value={u.userCode}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Mobile Number
              </label>
              <input
                type="text"
                maxLength={10}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.mobile || ''}
                onChange={(e) => handleFormChange('mobile', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="Enter mobile number"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                maxLength={100}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.email || ''}
                onChange={(e) => handleFormChange('email', e.target.value)}
                placeholder="Enter email address"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Address Line 1
              </label>
              <input
                type="text"
                maxLength={100}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.address1 || ''}
                onChange={(e) => handleFormChange('address1', e.target.value)}
                placeholder="Enter address line 1"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Address Line 2
              </label>
              <input
                type="text"
                maxLength={100}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.address2 || ''}
                onChange={(e) => handleFormChange('address2', e.target.value)}
                placeholder="Enter address line 2"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                City
              </label>
              <input
                type="text"
                maxLength={50}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.city || ''}
                onChange={(e) => handleFormChange('city', e.target.value)}
                placeholder="Enter city"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                State
              </label>
              <select
                value={formData.state || ''}
                disabled={readOnly}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    state: val,
                    stateName: val,
                  }));
                }}
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs cursor-pointer disabled:bg-slate-100 disabled:cursor-not-allowed"
              >
                <option value="">Select State</option>
                {stateList.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            <div className="text-left">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700">
                  Pincode
                </label>
                {formData.pincode && formData.pincode.length > 0 && formData.pincode.length < 6 && (
                  <span className="text-[10px] font-medium text-amber-600">
                    {6 - formData.pincode.length} more digit{6 - formData.pincode.length > 1 ? 's' : ''} required
                  </span>
                )}
              </div>
              <input
                type="text"
                maxLength={6}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.pincode || ''}
                onChange={(e) => handleFormChange('pincode', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="Enter 6-digit pincode"
                className={`w-full rounded-xl border bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed ${
                  formData.pincode && formData.pincode.length > 0 && formData.pincode.length < 6
                    ? 'border-amber-300 focus:border-amber-500 focus:ring-2 focus:ring-amber-100'
                    : 'border-slate-300 focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100'
                }`}
              />
              {formData.pincode && formData.pincode.length > 0 && formData.pincode.length < 6 && (
                <p className="mt-1 text-[11px] text-amber-600">
                  Pincode must be exactly 6 digits
                </p>
              )}
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                PAN Number <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                maxLength={10}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.pannumber || ''}
                onChange={(e) => handleFormChange('pannumber', e.target.value.toUpperCase())}
                placeholder="Enter 10-character PAN number"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 uppercase placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Bank Name
              </label>
              <input
                type="text"
                maxLength={100}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.bankName || ''}
                onChange={(e) => handleFormChange('bankName', e.target.value)}
                placeholder="Enter bank name"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Account Name
              </label>
              <input
                type="text"
                maxLength={100}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.accountName || ''}
                onChange={(e) => handleFormChange('accountName', e.target.value)}
                placeholder="Enter account holder name"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Account Number
              </label>
              <input
                type="text"
                maxLength={50}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.accountNumber || ''}
                onChange={(e) => handleFormChange('accountNumber', e.target.value)}
                placeholder="Enter bank account number"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                IFSC Code
              </label>
              <input
                type="text"
                maxLength={11}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.ifsc || ''}
                onChange={(e) => handleFormChange('ifsc', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                placeholder="Enter 11-character IFSC code (e.g. SBIN0001234)"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 uppercase placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                Bank Location
              </label>
              <input
                type="text"
                maxLength={100}
                disabled={readOnly}
                readOnly={readOnly}
                value={formData.bankLocation || ''}
                onChange={(e) => handleFormChange('bankLocation', e.target.value)}
                placeholder="Enter bank branch location"
                className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 shadow-2xs disabled:bg-slate-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="text-left">
              <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-700 mb-2">
                SEND REPORTS TO PATIENTS ?
              </label>
              <div className="flex items-center gap-6">
                <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-800 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="sendReportToPatient"
                    value="N"
                    disabled={readOnly}
                    checked={
                      String(formData.sendReportToPatient).toUpperCase() === 'N' ||
                      String(formData.sendReportToPatient).toUpperCase() === 'NO' ||
                      formData.sendReportToPatient === 0 ||
                      !formData.sendReportToPatient
                    }
                    onChange={() => handleFormChange('sendReportToPatient', 'N')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600 disabled:cursor-not-allowed"
                  />
                  NO
                </label>
                <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-800 cursor-pointer select-none">
                  <input
                    type="radio"
                    name="sendReportToPatient"
                    value="Y"
                    disabled={readOnly}
                    checked={
                      String(formData.sendReportToPatient).toUpperCase() === 'Y' ||
                      String(formData.sendReportToPatient).toUpperCase() === 'YES' ||
                      formData.sendReportToPatient === 1
                    }
                    onChange={() => handleFormChange('sendReportToPatient', 'Y')}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600 disabled:cursor-not-allowed"
                  />
                  YES
                </label>
              </div>
            </div>

            {!readOnly && formMode === 'edit' && (
              <div className="pt-2 text-left">
                <div className="flex items-center gap-3 rounded-xl border border-slate-300 bg-slate-50 p-3.5">
                  <input
                    type="checkbox"
                    id="statusCheckbox"
                    checked={statusCheckbox}
                    onChange={(e) => setStatusCheckbox(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <label htmlFor="statusCheckbox" className="text-xs font-bold text-slate-800 cursor-pointer select-none">
                    {formData.status === 1 ? 'Make Inactive' : 'Make Active'}
                  </label>
                </div>
              </div>
            )}

            {readOnly ? (
              <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-200 pt-5">
                {onExit && (
                  <button
                    type="button"
                    onClick={onExit}
                    className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 shadow-2xs transition"
                  >
                    Back to List
                  </button>
                )}
                {onReject && (
                  <button
                    type="button"
                    disabled={rejecting || approving}
                    onClick={() => onReject(formData.id || formData.code)}
                    className="flex items-center gap-2 rounded-xl bg-rose-600 px-6 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-rose-700 transition disabled:opacity-50 active:scale-98"
                  >
                    {rejecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    Reject Corporate
                  </button>
                )}
                {onApprove && (
                  <button
                    type="button"
                    disabled={approving || rejecting}
                    onClick={() => onApprove(formData.id || formData.code)}
                    className="flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 transition disabled:opacity-50 active:scale-98"
                  >
                    {approving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Approve Corporate
                  </button>
                )}
              </div>
            ) : (
              <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-200 pt-5">
                <button
                  type="button"
                  onClick={() => {
                    if (formMode === 'edit') {
                      setView('list');
                    } else {
                      setFormData(initialFormData);
                    }
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 shadow-2xs transition"
                >
                  {formMode === 'edit' ? 'Cancel' : 'Reset Form'}
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 rounded-xl bg-indigo-600 px-6 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-indigo-700 transition disabled:opacity-50 active:scale-98"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {formMode === 'create' ? 'Save Corporate' : 'Update Corporate'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );

  if (readOnly) {
    return <div className="py-2">{formContent}</div>;
  }

  return (
    <div className="min-h-screen w-full bg-slate-50 px-4 py-4 sm:px-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-900 text-lg">CureBay</span>
              <span className="text-sm font-medium text-slate-500">Corporate Onboarding</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {view === 'form' ? (
            <button
              type="button"
              onClick={() => setView('list')}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
            >
              <ListIcon className="h-4 w-4 text-indigo-600" /> View List
            </button>
          ) : (
            <button
              type="button"
              onClick={handleAddNew}
              className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition"
            >
              <Plus className="h-4 w-4" /> New Corporate
            </button>
          )}

          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <UserCircle2 className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-semibold text-slate-700">{currentUserName() || currentUserCode() || 'Admin'}</span>
          </div>
        </div>
      </div>

      {view === 'form' ? (
        formContent
      ) : (
        /* List View */
        <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
          {/* Compact Top Bar: Status Tabs (Left) + Quick Search & Popover Trigger (Right) */}
          <div className="sticky top-16 z-20 bg-white/95 backdrop-blur-xs pt-0.5 pb-2 mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100">
            {/* Quick Segment Filter Tabs */}
            <div className="inline-flex rounded-lg bg-slate-100/90 p-0.5 border border-slate-200/60">
              <button
                type="button"
                onClick={() => {
                  setFilters((prev) => ({ ...prev, approvalStatus: '', status: '' }));
                  setPage(0);
                }}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-bold transition ${
                  filters.approvalStatus === '' && !filters.status
                    ? 'bg-white text-indigo-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Building2 className="h-3 w-3" /> All
              </button>

              <button
                type="button"
                onClick={() => {
                  setFilters((prev) => ({ ...prev, approvalStatus: '0' }));
                  setPage(0);
                }}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-bold transition ${
                  filters.approvalStatus === '0'
                    ? 'bg-white text-amber-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Clock className="h-3 w-3 text-amber-500" /> Pending
              </button>

              <button
                type="button"
                onClick={() => {
                  setFilters((prev) => ({ ...prev, approvalStatus: '1' }));
                  setPage(0);
                }}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-bold transition ${
                  filters.approvalStatus === '1'
                    ? 'bg-white text-emerald-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <CheckCircle2 className="h-3 w-3 text-emerald-500" /> Approved
              </button>

              <button
                type="button"
                onClick={() => {
                  setFilters((prev) => ({ ...prev, approvalStatus: '2' }));
                  setPage(0);
                }}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-bold transition ${
                  filters.approvalStatus === '2'
                    ? 'bg-white text-rose-700 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <XCircle className="h-3 w-3 text-rose-500" /> Rejected
              </button>
            </div>

            {/* Right: Quick Search, Filter Popover Button, Total count */}
            <div className="flex items-center gap-2">
              {/* Quick Search input */}
              <div className="relative min-w-[200px] sm:min-w-[240px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={filters.name}
                  onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setPage(0);
                      loadCorporateList();
                    }
                  }}
                  placeholder="Search corporate name..."
                  className="w-full rounded-xl border border-slate-300 bg-white py-1.5 pl-8 pr-7 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                />
                {filters.name && (
                  <button
                    type="button"
                    onClick={() => {
                      setFilters((prev) => ({ ...prev, name: '' }));
                      setPage(0);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>

              {/* Filter Popover Button */}
              <div className="relative" ref={filterRef}>
                <button
                  type="button"
                  onClick={() => setFilterOpen((prev) => !prev)}
                  className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition ${
                    activeFilterCount > 0
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-700 shadow-2xs'
                      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <Search className="h-3.5 w-3.5 text-slate-500" />
                  <span>Filters</span>
                  {activeFilterCount > 0 && (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-[10px] font-bold text-white">
                      {activeFilterCount}
                    </span>
                  )}
                </button>

                {/* Floating Filter Popover */}
                {filterOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-[420px] rounded-2xl border border-slate-200 bg-white shadow-2xl ring-1 ring-slate-900/10 overflow-hidden animate-in fade-in zoom-in-95">
                    {/* Popover Header */}
                    <div className="flex items-center justify-between border-b border-slate-200/80 bg-slate-50/80 px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-2xs">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div className="text-left">
                          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                            Filter Corporates
                          </h3>
                          <p className="text-[11px] text-slate-500">Refine the corporate records</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {activeFilterCount > 0 && (
                          <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700">
                            {activeFilterCount} active
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setFilterOpen(false)}
                          className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200/70 hover:text-slate-700 transition"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Popover Form (2-column balanced grid) */}
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        setFilterOpen(false);
                        setPage(0);
                        loadCorporateList();
                      }}
                    >
                      <div className="p-5 space-y-3.5">
                        <div className="grid grid-cols-2 gap-3.5">
                          {/* Corporate Code */}
                          <div className="text-left">
                            <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                              Corporate Code
                            </label>
                            <input
                              type="text"
                              value={filters.code}
                              onChange={(e) => setFilters((prev) => ({ ...prev, code: e.target.value }))}
                              placeholder="e.g. CORP01"
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                            />
                          </div>

                          {/* Approval Status */}
                          <div className="text-left">
                            <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                              Approval Status
                            </label>
                            <select
                              value={filters.approvalStatus}
                              onChange={(e) => setFilters((prev) => ({ ...prev, approvalStatus: e.target.value }))}
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 cursor-pointer"
                            >
                              <option value="">All Statuses</option>
                              <option value="0">Pending</option>
                              <option value="1">Approved</option>
                              <option value="2">Rejected</option>
                            </select>
                          </div>

                          {/* Corporate Name (Full width span) */}
                          <div className="col-span-2 text-left">
                            <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                              Corporate Name
                            </label>
                            <div className="relative">
                              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                              <input
                                type="text"
                                value={filters.name}
                                onChange={(e) => setFilters((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder="Search by corporate name"
                                className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-8 pr-3 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                              />
                            </div>
                          </div>

                          {/* Email */}
                          <div className="text-left">
                            <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                              Email Address
                            </label>
                            <input
                              type="text"
                              value={filters.email}
                              onChange={(e) => setFilters((prev) => ({ ...prev, email: e.target.value }))}
                              placeholder="name@domain.com"
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                            />
                          </div>

                          {/* Mobile */}
                          <div className="text-left">
                            <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                              Mobile Number
                            </label>
                            <input
                              type="text"
                              maxLength={10}
                              value={filters.mobile}
                              onChange={(e) => setFilters((prev) => ({ ...prev, mobile: e.target.value.replace(/[^0-9]/g, '') }))}
                              placeholder="10-digit mobile"
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
                            />
                          </div>

                          {/* Account Status */}
                          <div className="col-span-2 text-left">
                            <label className="block text-left text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                              Status
                            </label>
                            <select
                              value={filters.status || ''}
                              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 outline-none transition focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 cursor-pointer"
                            >
                              <option value="">All Statuses (No status filter)</option>
                              <option value="1">Active</option>
                              <option value="0">Inactive</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Popover Footer Actions */}
                      <div className="flex items-center justify-between border-t border-slate-200/80 bg-slate-50/80 px-5 py-3">
                        <button
                          type="button"
                          onClick={() => {
                            handleResetFilters();
                          }}
                          className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          <span>Reset all</span>
                        </button>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setFilterOpen(false)}
                            className="rounded-xl border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition shadow-2xs"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-indigo-700 active:scale-98 transition"
                          >
                            <Search className="h-3.5 w-3.5" />
                            <span>Apply Filters</span>
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Reset shortcut if any filter active */}
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  title="Reset all filters"
                  className="flex h-8 w-8 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              )}

      
            </div>
          </div>

          {/* Table */}
          <div className="overflow-auto max-h-[calc(100vh-280px)] min-h-[350px] rounded-xl border border-slate-200 shadow-xs">
            <table className="w-full text-left text-xs border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 bg-slate-50 shadow-xs">
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Corporate Code</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Corporate Name</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Email</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Mobile</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 border-b border-slate-200">City</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 border-b border-slate-200">State</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 border-b border-slate-200">Created Date</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 text-center border-b border-slate-200">Status</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 text-center border-b border-slate-200">Approval</th>
                  <th className="sticky top-0 bg-slate-50 px-3.5 py-3 text-right border-b border-slate-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-slate-400">
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-indigo-600 mb-2" />
                      Loading corporate entities...
                    </td>
                  </tr>
                ) : corporateList.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-4 py-12 text-center text-slate-400">
                      No corporate records found. Click "+ New Corporate" to onboard one.
                    </td>
                  </tr>
                ) : (
                  corporateList.map((corp) => {
                    const isStatusActive = Number(corp.status) === 1;
                    const isApproved = Number(corp.isApproved);

                    return (
                      <tr
                        key={corp.id || corp.code}
                        onDoubleClick={() => handleViewEdit(corp)}
                        className="hover:bg-slate-50/80 transition cursor-pointer"
                      >
                        <td className="px-3.5 py-3 font-semibold text-indigo-600">{corp.code || '—'}</td>
                        <td className="px-3.5 py-3 font-bold text-slate-800">{corp.name || '—'}</td>
                        <td className="px-3.5 py-3 text-slate-600">{corp.email || '—'}</td>
                        <td className="px-3.5 py-3 text-slate-600">{corp.mobile || '—'}</td>
                        <td className="px-3.5 py-3 text-slate-600">{corp.city || '—'}</td>
                        <td className="px-3.5 py-3 text-slate-600">{corp.stateName || corp.state || '—'}</td>
                        <td className="px-3.5 py-3 text-slate-500 whitespace-nowrap">{formatDate(corp.createdDate)}</td>
                        <td className="px-3.5 py-3 text-center">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                              isStatusActive
                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                                : 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
                            }`}
                          >
                            {isStatusActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-3.5 py-3 text-center">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${
                              isApproved === 1
                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                                : isApproved === 2
                                ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
                                : 'bg-amber-50 text-amber-700 ring-1 ring-amber-200'
                            }`}
                          >
                            {isApproved === 1 ? 'Approved' : isApproved === 2 ? 'Rejected' : 'Pending'}
                          </span>
                        </td>
                        <td className="px-3.5 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleViewEdit(corp)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-indigo-600 shadow-xs hover:bg-indigo-50 hover:border-indigo-200 transition"
                          >
                            <Pencil className="h-3 w-3" /> View / Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && totalRecords > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4 mt-2">
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <div className="flex items-center gap-1.5">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(0);
                    }}
                    className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs outline-none"
                  >
                    {[10, 20, 50, 100].map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <span>
                  {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalRecords)} of {totalRecords} records
                </span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-xs font-semibold text-slate-700">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
